import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { SuccessToast } from '../SuccessToast'
import { PageHeader } from '../PageHeader'
import { ProductViewToggle } from './ProductViewToggle'
import { PreviewInterestVote } from './PreviewInterestVote'
import { mcpPreviewVoteFeature } from '../../lib/mcpVoteFeature'
import { RequiresSubscriptionBadge } from './RequiresSubscriptionBadge'
import {
  McpCatalogBody,
  McpPreviewGridCard,
  McpPreviewListRow,
  PreviewDisabledButton,
  useMcpCatalogState,
} from './mcpShared'
import type { MambuConnectionCatalogItem } from '../../lib/types'

/* Shared page shell for MCP Config and MCP Analytics.
   Both surfaces display the same catalog of MCP connections; only the live-item
   actions, avatar icon, vote surface identifier, and page copy differ. */

type SetToast = (message: string | null) => void

interface McpConnectionsPageProps {
  readonly heading: string
  readonly subtitle: string
  readonly failedToLoad: string
  readonly gridViewLabel: string
  readonly listViewLabel: string
  /* CSS class infix used for grid, list, and view-toggle elements (mcpconn | mcpobs). */
  readonly cssPrefix: 'mcpconn' | 'mcpobs'
  /* Which surface to record on interest-vote events. */
  readonly voteSurface: 'config' | 'analytics'
  /* Icon shown on the disabled action button for preview items. */
  readonly disabledActionIcon: ReactNode
  /* Called with the live item + the page's setToast so live-item actions (e.g. copy)
     can trigger toasts without lifting state out of this component. */
  readonly renderLiveCard: (item: MambuConnectionCatalogItem, setToast: SetToast) => ReactNode
  readonly renderLiveRow: (item: MambuConnectionCatalogItem, setToast: SetToast) => ReactNode
  /* Avatar renderer — sizes differ between grid (22) and list (18). */
  readonly renderAvatar: (size: 22 | 18) => ReactNode
}

export function McpConnectionsPage({
  heading, subtitle, failedToLoad, gridViewLabel, listViewLabel,
  cssPrefix, voteSurface, disabledActionIcon,
  renderLiveCard, renderLiveRow, renderAvatar,
}: McpConnectionsPageProps) {
  const { t } = useTranslation()
  const {
    view, setView,
    toast, setToast,
    voted, setVoted,
    items, isLoading, error,
    previewDescription, previewRequires, previewDisabledTitle,
  } = useMcpCatalogState()

  /* Builds the props shared by both the grid card and list row for preview items.
     Grid vs list differ only in the wrapper component and avatar size — everything
     else (votes, badge, disabled button, description) is identical, so building it
     once here prevents the token-level duplication Sonar CPD would otherwise flag. */
  function previewItemProps(item: MambuConnectionCatalogItem) {
    return {
      item,
      reqBadge: <RequiresSubscriptionBadge label={previewRequires(item)} />,
      description: previewDescription(item),
      voteButtons: (
        <PreviewInterestVote
          feature={mcpPreviewVoteFeature(item.key, voteSurface)}
          voted={voted[item.key] ?? null}
          onVote={(direction) => setVoted((prev) => ({ ...prev, [item.key]: direction }))}
          onVoted={() => setToast(t('mambuMcp.preview.voteThanksToast', { feature: item.name }))}
        />
      ),
      actionButton: <PreviewDisabledButton title={previewDisabledTitle(item)} icon={disabledActionIcon} />,
    }
  }

  function renderGridCard(item: MambuConnectionCatalogItem) {
    if (item.status === 'live') return renderLiveCard(item, setToast)
    return <McpPreviewGridCard key={item.key} {...previewItemProps(item)} avatar={renderAvatar(22)} />
  }

  function renderListRow(item: MambuConnectionCatalogItem) {
    if (item.status === 'live') return renderLiveRow(item, setToast)
    return <McpPreviewListRow key={item.key} {...previewItemProps(item)} avatar={renderAvatar(18)} />
  }

  const viewToggle = (
    <ProductViewToggle
      view={view}
      onChange={setView}
      gridLabel={gridViewLabel}
      listLabel={listViewLabel}
      btnClassName={`${cssPrefix}-view-btn`}
    />
  )

  return (
    <div className="page-view">
      <div className="page-header">
        <PageHeader title={heading} caption={subtitle} actions={viewToggle} />
      </div>{/* end .page-header */}

      <McpCatalogBody isLoading={isLoading} error={error} errorMessage={failedToLoad}>
        <div className={`agents-grid ${cssPrefix}-grid${view === 'grid' ? '' : ' hidden'}`}>
          {items.map(renderGridCard)}
        </div>
        <div className={`agents-list ${cssPrefix}-list${view === 'list' ? ' active' : ''}`}>
          {items.map(renderListRow)}
        </div>
      </McpCatalogBody>

      {toast && <SuccessToast message={toast} />}
    </div>
  )
}
